import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import {
  CLIENT_CONFIGS,
  applyClientQuirks,
  simulateImagesOff,
  type EmailClient,
} from "@ease/page-builder";

type PreviewShellMode = "standard" | "images_off" | "dark";

interface EmailPreviewProps {
  html: string;
  title?: string;
}

const SHELL_MODES: Array<{
  id: PreviewShellMode;
  label: string;
}> = [
  { id: "standard", label: "Standard" },
  { id: "images_off", label: "Images off" },
  { id: "dark", label: "Dark shell" },
];

const CLIENT_BUTTONS = CLIENT_CONFIGS.map((config) => ({
  id: config.client,
  label: config.label,
  widthClassName: config.viewport.width <= 390 ? "w-[375px] max-w-full" : "w-full max-w-[600px]",
}));

const FALLBACK_CLIENT_CONFIG = CLIENT_CONFIGS[0];
const FALLBACK_CLIENT_BUTTON = CLIENT_BUTTONS[0];

function buildPreviewDocument(html: string, mode: PreviewShellMode): string {
  const shellStyles = mode === "dark"
    ? `
      :root { color-scheme: dark; }
      body {
        margin: 0;
        background: #0f172a;
        color: #e2e8f0;
      }
      a { color: #7dd3fc; }
    `
    : `
      body {
        margin: 0;
        background: #ffffff;
      }
    `;

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      ${shellStyles}
    </style>
  </head>
  <body>${html}</body>
</html>`;
}

export function EmailPreview({ html, title = "Email preview" }: EmailPreviewProps) {
  const [client, setClient] = useState<EmailClient>("gmail_web");
  const [mode, setMode] = useState<PreviewShellMode>("standard");

  const clientConfig = CLIENT_CONFIGS.find((entry) => entry.client === client) ?? FALLBACK_CLIENT_CONFIG;
  const activeViewport = CLIENT_BUTTONS.find((entry) => entry.id === client) ?? FALLBACK_CLIENT_BUTTON;
  if (!clientConfig || !activeViewport) {
    return null;
  }
  const previewHtml = useMemo(() => {
    const clientHtml = applyClientQuirks(html, client);
    return mode === "images_off" ? simulateImagesOff(clientHtml) : clientHtml;
  }, [client, html, mode]);
  const srcDoc = useMemo(() => buildPreviewDocument(previewHtml, mode), [previewHtml, mode]);

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Email client
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {CLIENT_BUTTONS.map((viewport) => (
            <button
              key={viewport.id}
              type="button"
              onClick={() => setClient(viewport.id)}
              className={cn(
                "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                client === viewport.id
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:bg-accent hover:text-foreground",
              )}
            >
              {viewport.label}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Review mode
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {SHELL_MODES.map((viewport) => (
            <button
              key={viewport.id}
              type="button"
              onClick={() => setMode(viewport.id)}
              className={cn(
                "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                mode === viewport.id
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:bg-accent hover:text-foreground",
              )}
            >
              {viewport.label}
            </button>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-border bg-[var(--color-surface-subtle,var(--color-muted))] p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-foreground">{clientConfig.label}</p>
            <p className="text-xs text-muted-foreground">
              Approximate viewport {clientConfig.viewport.width}px x {clientConfig.viewport.height}px
            </p>
          </div>
          <div className="inline-flex rounded-full border border-border bg-background px-3 py-1 text-xs text-muted-foreground">
            {mode === "images_off" ? "Images blocked" : mode === "dark" ? "Dark shell" : "Standard shell"}
          </div>
        </div>
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-950">
          <p className="font-medium">Known rendering risks</p>
          <ul className="mt-2 space-y-1 text-xs leading-5 text-amber-900">
            {clientConfig.quirks.map((quirk) => (
              <li key={quirk.description}>• {quirk.description}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className="rounded-xl border border-border bg-slate-100/80 p-4 dark:bg-slate-950/40">
        <div className="mx-auto flex justify-center">
          <iframe
            title={title}
            srcDoc={srcDoc}
            sandbox="allow-same-origin"
            className={cn(
              "h-[720px] rounded-xl border border-border bg-white shadow-sm transition-all dark:bg-slate-950",
              activeViewport?.widthClassName,
            )}
          />
        </div>
      </div>
    </div>
  );
}

export {
  buildPreviewDocument,
};
